import type { ReactNode } from 'react';

import type { themeVars } from '@shieldpay/theme-provider-ui/theme/contract';
import type { Palette } from '@shieldpay/theme-provider-ui/theme/types';
import { classFilter } from '@shieldpay/theme-provider-ui/utilities';

import CloseIcon from '../icons/CloseIcon';
import {
  DocumentUploaded,
  DocumentUploading,
} from '../images/multi-colour-icons';

import { Box } from '../box/box';
import { Button } from '../button/button';
import { Card } from '../card/card';
import { LoadingSpinner } from '../loading-spinner/loading-spinner';
import { Text } from '../text/text';

import * as styles from './list.css';

export interface ListProps {
  children: ReactNode;
  ordered?: boolean;
}

interface TextContentProps {
  children: ReactNode;
  label?: ReactNode;
  labelColor?: Palette;
  color?: Palette;
  variant?: keyof typeof themeVars.typography.variant | 'inherit';
}

interface DefaultListItemProps {
  children: ReactNode;
  LeftIcon?: ReactNode;
  RightIcon?: ReactNode;
  position?: never;
  label: ReactNode;
  filename?: never;
  deleteDocumentText?: never;
  uploadingText?: never;
  state?: never;
  onClear?: never;
}

interface DisplayListItemProps {
  children: ReactNode;
  LeftIcon: ReactNode;
  RightIcon?: ReactNode;
  position?: never;
  label?: ReactNode;
  filename?: never;
  deleteDocumentText?: never;
  uploadingText?: never;
  state?: never;
  onClear?: never;
}

interface TimelineListItemProps {
  children: ReactNode;
  LeftIcon: ReactNode;
  RightIcon?: ReactNode;
  position?: 'default' | 'first' | 'last';
  label?: ReactNode;
  filename?: never;
  deleteDocumentText?: never;
  uploadingText?: never;
  state?: never;
  onClear?: never;
}

export interface UploaderListItemProps {
  children?: never; // use filename instead
  LeftIcon?: never;
  RightIcon?: never;
  position?: never;
  label?: never; // use uploadingText instead
  filename?: ReactNode;
  deleteDocumentText: ReactNode;
  uploadingText: ReactNode;
  state: 'default' | 'uploading' | 'uploaded';
  onClear: () => void;
}

export type ListItemProps =
  | ({ variant?: 'default' } & DefaultListItemProps)
  | ({ variant: 'display' } & DisplayListItemProps)
  | ({ variant: 'timeline' } & TimelineListItemProps)
  | ({ variant: 'uploader' } & UploaderListItemProps);

const TextContent = ({
  children,
  label,
  labelColor,
  color,
  variant,
}: TextContentProps) => (
  <Box
    expand
    spacing="baseNeg5"
    constrain="clipX"
    size={{
      minWidth: 'none',
    }}
    alignContent={{ y: 'center' }}
  >
    {label ? (
      <Text color={labelColor} variant={variant} truncate>
        {label}
      </Text>
    ) : null}
    <Text color={color} variant="body" truncate>
      {children}
    </Text>
  </Box>
);

const DefaultListItem = ({
  children,
  LeftIcon,
  RightIcon,
  label,
}: DefaultListItemProps) => (
  <Box
    Component="li"
    stack="row"
    alignContent={{ y: 'center' }}
    spacing="baseNeg3"
  >
    {LeftIcon}
    <TextContent
      label={label}
      labelColor={'neutral400'}
      variant={'listItemLabel'}
    >
      {children}
    </TextContent>
    {RightIcon}
  </Box>
);

const DisplayListItem = ({
  children,
  LeftIcon,
  RightIcon,
  label,
}: DisplayListItemProps) => (
  <Card
    Component="li"
    boxVariant="grid"
    alignContent={{ x: 'left', y: 'center' }}
    padding={{ y: 'baseNeg4', left: 'baseNeg4', right: 'baseNeg2' }}
    spacing="base"
    className={styles.gridColumns}
    constrain="clipX"
  >
    {LeftIcon}
    <TextContent label={label}>{children}</TextContent>
    {RightIcon}
  </Card>
);

const TimelineListItem = ({
  children,
  LeftIcon,
  RightIcon,
  position,
  label,
}: TimelineListItemProps) => (
  <Box
    Component="li"
    spacing="base"
    variant="grid"
    constrain="clipX"
    className={classFilter([styles.timeline, styles.gridColumns])}
  >
    <Box
      Component="span"
      alignContent={{ x: 'center', y: 'full' }}
      gridItem={{
        columnStart: '1',
        rowStart: position === 'first' ? '2' : '1',
        rowEnd: position === 'default' ? '3' : undefined,
      }}
      className={classFilter([
        styles.line,
        position === 'last' && styles.lineLast,
      ])}
    />
    <Box
      Component="span"
      alignContent={{ x: 'center', y: 'center' }}
      gridItem={{
        columnStart: '1',
        rowStart: '1',
        rowEnd: '3',
      }}
    >
      {/* we give this a white background to knock out the line */}
      <Box background="neutral000">{LeftIcon}</Box>
    </Box>
    <Box
      gridItem={{ columnStart: '2', rowStart: '1', rowEnd: '3' }}
      stack="column"
      spacing="baseNeg6"
      alignContent={{ x: 'left' }}
    >
      {label ? <Text variant="caption">{label}</Text> : null}
      {children}
    </Box>
    {RightIcon ? (
      <Box
        gridItem={{
          rowStart: '1',
          rowEnd: '3',
        }}
      >
        {RightIcon}
      </Box>
    ) : null}
  </Box>
);

const UploaderListItem = ({
  filename,
  state,
  deleteDocumentText,
  uploadingText,
  onClear,
}: UploaderListItemProps) => (
  <Card
    Component="li"
    boxVariant="grid"
    alignContent={{ y: 'center' }}
    padding={{ left: 'baseNeg4', right: 'none' }}
    spacing="base"
    className={styles.gridColumns}
    constrain="clipX"
  >
    {/* only apply vertical padding to this element to keep height consistent between states */}
    <Box padding={{ y: 'baseNeg4' }}>
      {state === 'uploaded' ? <DocumentUploaded /> : <DocumentUploading />}
    </Box>

    {state === 'uploading' ? (
      <>
        <TextContent
          color="neutral300"
          label={uploadingText}
          labelColor="neutral300"
        >
          {filename}
        </TextContent>
        <Box padding={{ x: 'base' }}>
          <LoadingSpinner size="small" />
        </Box>
      </>
    ) : null}

    {state === 'uploaded' ? (
      <>
        <TextContent>{filename}</TextContent>
        <Button variant="icon" onClick={onClear} Icon={CloseIcon}>
          {deleteDocumentText}
        </Button>
      </>
    ) : null}
  </Card>
);

const ListItem = ({
  children,
  variant = 'default',
  position = 'default',
  LeftIcon,
  RightIcon,
  label,
  filename,
  deleteDocumentText,
  uploadingText,
  state = 'default',
  onClear,
}: ListItemProps) => {
  if (variant === 'default') {
    return (
      <DefaultListItem LeftIcon={LeftIcon} label={label} RightIcon={RightIcon}>
        {children}
      </DefaultListItem>
    );
  }

  if (variant === 'uploader') {
    if (!onClear) {
      throw new Error('missing onClear prop for upload list item');
    }

    return (
      <UploaderListItem
        deleteDocumentText={deleteDocumentText}
        state={state}
        uploadingText={uploadingText}
        filename={filename}
        onClear={onClear}
      />
    );
  }

  if (!LeftIcon) {
    throw new Error('no LeftIcon');
  }

  if (variant === 'display') {
    return (
      <DisplayListItem LeftIcon={LeftIcon} label={label} RightIcon={RightIcon}>
        {children}
      </DisplayListItem>
    );
  }

  return (
    <TimelineListItem
      position={position}
      LeftIcon={LeftIcon}
      label={label}
      RightIcon={RightIcon}
    >
      {children}
    </TimelineListItem>
  );
};

export const List = ({ children, ordered = false }: ListProps) => {
  const listType = ordered ? 'ol' : 'ul';

  return (
    <Box Component={listType} spacing="basePos2" expand>
      {children}
    </Box>
  );
};

List.Item = ListItem;
